import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  LayoutDashboard, 
  Briefcase, 
  Wrench,
  CreditCard,
  Settings,
  Menu,
  X,
  Plus
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import CompanySwitcher from './CompanySwitcher';

const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/portal',
      exact: true
    },
    {
      label: 'GoJobs',
      icon: Briefcase,
      path: '/portal/gojobs',
    },
    {
      label: 'GoTradies',
      icon: Wrench,
      path: '/portal/gotradies',
    },
    {
      label: 'Billing',
      icon: CreditCard,
      path: '/portal/billing',
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/portal/settings',
    },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 z-40 ${
        sidebarOpen ? 'w-64' : 'w-0 -translate-x-full md:translate-x-0 md:w-16'
      }`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            {sidebarOpen && (
              <button onClick={() => navigate('/')} className="text-xl font-bold text-foreground">
                GoFella Portal
              </button>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Quick Actions */}
          {sidebarOpen && (
            <div className="p-4 border-b border-border space-y-3">
              {/* Company Switcher */}
              <CompanySwitcher />
              
              {/* Quick Action Buttons */}
              <button
                onClick={() => navigate('/portal/gojobs/new')}
                className="w-full flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Post Job
              </button>
              <button
                onClick={() => navigate('/portal/gotradies/new')}
                className="w-full flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Create Service
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {sidebarOpen && <span className="font-medium">{item.label}</span>}
                </button>
              ))}
            </div>
          </nav>

          {/* User Info */}
          {sidebarOpen && user && (
            <div className="p-4 border-t border-border">
              <div className="text-sm text-muted-foreground mb-1">Logged in as</div>
              <div className="font-semibold text-foreground truncate">{user.full_name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-background/95 backdrop-blur border-b border-border">
          <div className="h-full px-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden p-2 hover:bg-muted rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-foreground">Client Portal</h1>
            </div>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <button
                onClick={logout}
                className="px-4 py-2 border border-border rounded-xl hover:bg-muted text-foreground"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;
